/**
 * Frequency and noise control for PostHog `$exception` events (the global
 * fallback: unhandled errors / rejections, Vue and router errors).
 *
 * PostHog already rate-limits autocaptured exceptions per exception type; this
 * is stricter and also covers manual captureException calls:
 * - known-harmless noise is dropped,
 * - the same type + message is reported once per page load,
 * - at most EXCEPTION_PAGE_CAP exceptions are reported per page load.
 */

import type { CaptureResult } from 'posthog-js'

export const EXCEPTION_PAGE_CAP = 20

// Browser-benign or information-free errors we never act on. Anything else found
// noisy later goes into PostHog's Error Tracking suppression rules (no deploy).
const IGNORED_EXCEPTIONS: Array<{ type?: RegExp, message?: RegExp }> = [
  { message: /^ResizeObserver loop (?:limit exceeded|completed with undelivered notifications)/ },
  // Cross-origin script errors carry no message, stack or source.
  { message: /^Script error\.?$/ },
  // Deliberately cancelled work: leaving a page, closing a preview.
  { type: /^AbortError$/ },
]

function firstException(event: CaptureResult): { type: string, message: string } | null {
  const list = event.properties?.$exception_list
  const first = Array.isArray(list) ? list[0] : null
  if (!first || typeof first !== 'object')
    return null
  return {
    type: typeof first.type === 'string' ? first.type : '',
    message: typeof first.value === 'string' ? first.value : '',
  }
}

export function createExceptionFilter(cap = EXCEPTION_PAGE_CAP) {
  const seen = new Set<string>()
  return (event: CaptureResult | null): CaptureResult | null => {
    if (!event || event.event !== '$exception')
      return event
    const exception = firstException(event)
    if (!exception)
      return event
    const { type, message } = exception
    if (IGNORED_EXCEPTIONS.some(rule => (!rule.type || rule.type.test(type)) && (!rule.message || rule.message.test(message))))
      return null
    const key = `${type}:${message}`
    if (seen.has(key) || seen.size >= cap)
      return null
    seen.add(key)
    return event
  }
}

export const filterExceptionEvent = createExceptionFilter()
